import type { ReadableStream } from 'web-streams-polyfill';

import type {
  ApplyAcls,
  CreateVersionParams,
} from '@iconik-io/iconik-api/src/sdk/assets';
import type { FilesProxyCreateSchema } from '@iconik-io/iconik-api/src/services/files';

import type { KeyValueStorageInterface } from '../storage/key_value_store';
import type { GetSourceStreamOptions } from '../types';

export const UPLOADER_CHUNK_STATUS_WAITING = 'WAITING' as const;
export const UPLOADER_CHUNK_STATUS_UPLOADING = 'UPLOADING' as const;
export const UPLOADER_CHUNK_STATUS_COMPLETED = 'COMPLETED' as const;
export const UPLOADER_CHUNK_STATUS_FAILED = 'FAILED' as const;

export const UPLOADER_STATUS_WAITING = 'WAITING' as const;
export const UPLOADER_STATUS_UPLOADING = 'UPLOADING' as const;
export const UPLOADER_STATUS_COMPLETED = 'COMPLETED' as const;
export const UPLOADER_STATUS_COMPLETING = 'COMPLETING' as const;
export const UPLOADER_STATUS_PAUSED = 'PAUSED' as const;
export const UPLOADER_STATUS_FAILED = 'FAILED' as const;

/**
 * This value will be different for different platforms.
 * For Node.js it's a string representing a full file path. In a browser it
 * refers to a File the app keeps outside the state, since a File cannot be
 * serialized.
 * Must be serializable for persistent uploader state storage.
 */
export type StreamSource =
  | {
      type: 'path';
      path: string;
    }
  | {
      type: 'fileRef';
      id: string;
    };

export type GetSourceStream = (
  streamSource: StreamSource,
  options: GetSourceStreamOptions,
) => ReadableStream;

export type CalculateChecksum = (
  streamSource: StreamSource,
  signal: AbortSignal,
) => Promise<string>;

export type FileInfo = {
  /**
   * File name, optionally with a relative path in case of uploading a directory. This should
   * always be a posix path no matter platform.
   */
  targetPath: string;

  /**
   * File size.
   */
  size: number;

  /**
   * MIME type of the file that will be used to create a format entity.
   */
  mimeType: string;

  /**
   * Source descriptor for the uploader data.
   */
  streamSource: StreamSource;
};

export type UploadOptions = {
  chunkSize?: number;
  numConcurrentChunks?: number;
  /**
   * Storage to upload to. An upload can be queued before one is chosen, but
   * running it without one fails with NoStorageSelectedError.
   */
  storageId?: string;
  /**
   * Collection that we should upload to. There might be sub collections created in this collection depending on targetPath in FileInfo
   * A path will be created based on the path to this collection and used as path on the storage. This can be overridden using basePath below.
   */
  baseCollectionId?: string;
  /**
   * Base path to use when uploading. If this is set it will override the path deduced from baseCollectionId above.
   */
  basePath?: string;
  customAssetTitle?: string;
  customFileName?: string;
  transcodePriority?: number;
  assetACL?: ApplyAcls;
  collectionACL?: ApplyAcls;
  /**
   * Calculate a checksum of the file before uploading it.
   */
  calcUploadChecksum?: boolean;
  /**
   * When the checksum matches a file on the same storage add that asset
   * to the destination collection instead of uploading the file again.
   */
  useChecksumMatches?: boolean;
  /**
   * What to ask iconik for once the file is closed. 'auto' derives it from the
   * target and the upload kind, which is what web needs; the explicit values
   * exist so that cli and agent uploads stay deterministic.
   */
  postCloseAction?: PostCloseAction | 'auto';
};

/**
 * Work that turns a closed file into something the user can see: keyframes
 * (which also produce a proxy), or media info for an asset that already has
 * proxies. Subtitles need neither.
 */
export type PostCloseAction = 'keyframes' | 'mediainfo' | 'none';

export type UploaderChunkStatus =
  | typeof UPLOADER_CHUNK_STATUS_WAITING
  | typeof UPLOADER_CHUNK_STATUS_UPLOADING
  | typeof UPLOADER_CHUNK_STATUS_COMPLETED
  | typeof UPLOADER_CHUNK_STATUS_FAILED;

export type UploaderChunkState<ChunkMeta> = Readonly<{
  index: number;
  status: UploaderChunkStatus;
  start: number;
  size: number;
  uploaded: number;
  meta?: ChunkMeta;
}>;

export type UploaderStatus =
  | typeof UPLOADER_STATUS_WAITING
  | typeof UPLOADER_STATUS_UPLOADING
  | typeof UPLOADER_STATUS_COMPLETED
  | typeof UPLOADER_STATUS_COMPLETING
  | typeof UPLOADER_STATUS_PAUSED
  | typeof UPLOADER_STATUS_FAILED;

type UploaderTargetNewAsset = {
  type: 'newAsset';
  assetId?: string;
};

type UploaderTargetNewVersion = {
  type: 'newVersion';
  versionId?: string;
} & CreateVersionParams;

/**
 * An asset that already exists and stays as it is: the upload only adds a
 * format to it. Covers proxies, subtitles, extra formats and filling a
 * placeholder.
 *
 * Cancelling such an upload must never touch the asset itself, only the
 * entities the upload created. Modelling these as `newAsset` would delete the
 * user's asset on cancel.
 */
type UploaderTargetExistingAsset = {
  type: 'existingAsset';
  assetId: string;
  /**
   * The asset is a PLACEHOLDER waiting for its file. It is switched to ASSET
   * before the bytes move, and switched back if the upload is cancelled or
   * fails terminally.
   */
  fillsPlaceholder?: boolean;
};

/*
 * When uploading we allow to either upload to an entirely new asset which will be created as part fo the upload.
 * Or to upload as a new version of an existing asset, or into an existing asset
 * that the upload leaves in place.
 */
export type UploaderTarget =
  | UploaderTargetNewAsset
  | UploaderTargetNewVersion
  | UploaderTargetExistingAsset;

export type UploaderTargetType = UploaderTarget['type'];

/**
 * Uploading a file into a format on the asset. Custom formats and subtitles
 * share this kind deliberately: they differ only in the format parameters and,
 * for subtitles, one extra call once the file is closed.
 */
export type FileUploadKind = {
  kind: 'file';
  /** Name of the format to create, ORIGINAL when omitted. */
  formatName?: string;
  /**
   * Extra metadata entries for the created format, the language of a subtitle
   * for instance. Unlike format components these cannot be corrected with a
   * later PATCH, so they are sent when the format is created.
   */
  formatMetadata?: Record<string, string>[];
  /** Store the file under a slugified name. */
  slugifyFileName?: boolean;
  /** Register the uploaded file as the asset's subtitles once it is closed. */
  subtitles?: {
    createTranscription: boolean;
    deleteOldTranscriptions?: boolean;
  };

  /**
   * The entities this upload creates on the asset, filled in as it progresses.
   * They live on the kind so that a proxy upload cannot reach them: it creates
   * no format, file set or file.
   */
  formatId?: string;
  fileSetId?: string;
  fileId?: string;
};

/**
 * Uploading a proxy for the asset. The payload iconik needs to create the proxy
 * is supplied by the consumer and sent almost verbatim: it carries product
 * decisions the package has no basis to make, such as visibility, the name the
 * user chose and which version the proxy belongs to.
 */
export type ProxyUploadKind = {
  kind: 'proxy';
  create: FilesProxyCreateSchema;
  /** Id of the proxy the bytes go into, once it has been created. */
  proxyId?: string;
};

/** What the upload creates on the asset. */
export type UploadKind = FileUploadKind | ProxyUploadKind;

export type UploadKindType = UploadKind['kind'];

export type UploaderState<Meta = unknown, ChunkMeta = unknown> = Readonly<{
  /**
   * Internal ID of the uploader item.
   */
  id: string;

  /**
   * File info structure that describes a file associated with the upload.
   */
  fileInfo: FileInfo;

  /**
   * Options that were used to initialize this uploader item state.
   */
  options: UploadOptions;

  /*
   * Controls which asset we should be uploading to.
   */
  target: UploaderTarget;

  /*
   * Controls what the upload creates on that asset. Queueing an upload without
   * one defaults to an ORIGINAL file.
   */
  uploadKind: UploadKind;

  status: UploaderStatus;

  /*
   * Checksum of the file, if calculated.
   */
  checksum?: string;

  /*
   * The upload completed without transferring the file because a file with
   * the same checksum was found.
   */
  skippedTransferForChecksum?: boolean;

  meta?: Meta;

  chunks?: UploaderChunkState<ChunkMeta>[];
}>;

/** Must be synchronous and pure, since it runs inside an atomic update. */
export type UploaderStateUpdater = (state: UploaderState) => UploaderState;

export interface UploaderStateStorageInterface
  extends KeyValueStorageInterface<string, UploaderState> {
  /**
   * Atomic read-modify-write of one state: reads it, applies the updater and
   * writes the result with no await in between, so nothing can delete or change
   * it in the meantime. A get followed by a set would let a removed upload come
   * back, or let two changes overwrite each other. Resolves with the new state,
   * or undefined when there is no state to change, which keeps a late write
   * from bringing back a removed upload.
   *
   * The guarantee is only within one JavaScript thread, and the type cannot
   * enforce it: each implementation has to keep the read and the write in the
   * same synchronous step.
   */
  update(
    stateId: string,
    updater: UploaderStateUpdater,
  ): Promise<UploaderState | undefined>;
}
